import type { Deal } from "../types";

/** Deals with no meaningful update for this many days count as stalled. */
export const STALE_DEAL_DAYS = 14;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const TERMINAL_STAGES = new Set(["fechado", "perdido"]);

type DealActivity = Pick<Deal, "stage" | "archived_at" | "last_activity_at">;

export function staleActivityCutoff(now: Date, days = STALE_DEAL_DAYS): string {
  return new Date(now.getTime() - days * MS_PER_DAY).toISOString();
}

export function daysSinceActivity(lastActivityAt: string, now: Date): number {
  return Math.floor((now.getTime() - Date.parse(lastActivityAt)) / MS_PER_DAY);
}

export function isStaleDeal<T extends DealActivity>(
  deal: T,
  now: Date,
  days = STALE_DEAL_DAYS,
): deal is T & { last_activity_at: string } {
  if (deal.archived_at) {
    return false;
  }
  if (TERMINAL_STAGES.has(deal.stage)) {
    return false;
  }
  if (!deal.last_activity_at) {
    return false;
  }
  const activityAt = Date.parse(deal.last_activity_at);
  if (Number.isNaN(activityAt)) {
    return false;
  }
  return now.getTime() - activityAt >= days * MS_PER_DAY;
}

export function selectStaleDeals<T extends DealActivity>(
  deals: T[],
  now: Date,
  days = STALE_DEAL_DAYS,
): Array<T & { last_activity_at: string }> {
  return deals
    .filter((deal): deal is T & { last_activity_at: string } =>
      isStaleDeal(deal, now, days),
    )
    .sort(
      (a, b) => Date.parse(a.last_activity_at) - Date.parse(b.last_activity_at),
    );
}
